import _ from 'lodash';
import React, { Component, PropTypes } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import { SearchBar, Pagination, Loader, Button, Badge, Customer, Icon } from '../../components';
import AddEditCustomerPane from './AddEditPane';
import LinkCustomerPane from './LinkPane';

import '../../../scss/containers/customers/_list.scss';

const DEFAULT_LIMIT = 10;

export class CustomerList extends Component {
	static propTypes = {
		user: PropTypes.shape({
			can: PropTypes.func.isRequired,
			id: PropTypes.string,
		}).isRequired,
		location: PropTypes.object,
		deleteCustomer: PropTypes.func,
		updateCustomers: PropTypes.func,
		myCustomersQuery: PropTypes.object,
		notMyCustomersQuery: PropTypes.object,
	};

	state = {
		selected: {
			myCustomers: new Set(),
			notMyCustomers: new Set(),
		},
		deletingCustomerIds: [],
		showAddEditPane: false,
		customer: null,
	};

	componentDidMount() {
		if (!this.props.user.can('view-customer-list')) {
			browserHistory.push('/');
		}
	}

	handleSearch(filter) {
		browserHistory.push({
			...this.props.location,
			query: {
				...this.props.location.query,
				myCustomersPage: 1,
				notMyCustomersPage: 1,
				filter,
			},
		});
	}

	handleChangeSelection(list, customer, isSelected) {
		const selected = this.state.selected;

		if (isSelected) {
			selected[list].add(customer.id);
		} else {
			selected[list].delete(customer.id);
		}

		this.setState({ selected });
	}

	async handleClickLinkBtn() {
		if (this.state.selected.notMyCustomers.size) {
			await this.props.updateCustomers(
				this.state.selected.notMyCustomers,
				this.props.user.id,
			);

			this.setState({
				selected: {
					myCustomers: new Set(),
					notMyCustomers: new Set(),
				},
			});

			this.refetchCustomers();
		}
	}

	async handleClickUnlinkBtn() {
		if (this.state.selected.myCustomers.size) {
			await this.props.updateCustomers(this.state.selected.myCustomers, null);

			this.setState({
				selected: {
					myCustomers: new Set(),
					notMyCustomers: new Set(),
				},
			});

			this.refetchCustomers();
		}
	}

	handleClosePane(refreshList = false) {
		this.setState({
			showAddEditPane: false,
			showLinkPane: false,
			customer: null,
		});

		if (refreshList) {
			this.refetchCustomers();
		}
	}

	handleClickAddCustomer() {
		this.setState({ showAddEditPane: true });
	}

	handleClickEditCustomer(customer) {
		this.setState({ showAddEditPane: true, customer });
	}

	async handleClickDeleteCustomer(customer) {
		if (confirm('Are you sure you want to delete this customer?')) {
			this.setState(
				{
					deletingCustomerIds: [customer.id, ...this.state.deletingCustomerIds],
				},
				async () => {
					await this.props.deleteCustomer(customer.id);
					this.setState({
						deletingCustomerIds: this.state.deletingCustomerIds.filter(
							c => c.id !== customer.id,
						),
					});
					this.refetchCustomers();
				},
			);
		}
	}

	handleClickManageCustomerLinks(customer) {
		this.setState({ showLinkPane: true, customer });
	}

	handleChangePage(list, page) {
		const paramName = list + 'Page';

		browserHistory.push({
			...this.props.location,
			query: {
				...this.props.location.query,
				[paramName]: page,
			},
		});
	}

	refetchCustomers() {
		return Promise.all([
			this.props.myCustomersQuery.refetch(),
			this.props.notMyCustomersQuery.refetch(),
		]);
	}

	createCustomerComponent(customer, list) {
		return (
			<Customer
				key={`customer-${customer.id}`}
				customer={customer}
				isSelected={this.state.selected[list].has(customer.id)}
				onChangeSelection={isSelected => {
					this.handleChangeSelection(list, customer, isSelected);
				}}
				canEdit={this.props.user.can('edit-customer-list')}
				onEdit={() => this.handleClickEditCustomer(customer)}
				isDeleting={this.state.deletingCustomerIds.indexOf(customer.id) > -1}
				canDelete={this.props.user.can('edit-customer-list')}
				onDelete={() => this.handleClickDeleteCustomer(customer)}
				onManageLinks={() => this.handleClickManageCustomerLinks(customer)}
			/>
		);
	}

	render() {
		const {
			myCustomersQuery,
			notMyCustomersQuery,
			location: { query },
		} = this.props;

		const isFetching = myCustomersQuery.loading || notMyCustomersQuery.loading;
		const myCustomers = _.get(myCustomersQuery, 'customers.results') || [];
		const notMyCustomers = _.get(notMyCustomersQuery, 'customers.results') || [];

		return (
			<div className="container-fluid customer-list-container">
				<div className="row">
					<div className="cell">
						<h1>Customers</h1>

						<If condition={this.props.user.can('edit-customer-list')}>
							<Button action="add" onClick={e => this.handleClickAddCustomer(e)}>
								Add
							</Button>
						</If>
					</div>
					<div className="cell justify-center">
						<Loader visible={isFetching} />
					</div>
					<div className="cell justify-right">
						<SearchBar
							value={query.filter || ''}
							onEnter={filter => this.handleSearch(filter)}
						/>
					</div>
				</div>
				<div className="customer-lists">
					<div className="customer-lists-left">
						<h2>
							My Customers
							<If condition={myCustomers.length}>
								<ReactCSSTransitionGroup
									transitionName="changed"
									transitionEnterTimeout={2000}
									transitionLeaveTimeout={100}>
									<Badge
										key={'badge-' + this.state.lastBulkEditTimestamp}
										shape="square"
										style="gray">
										{myCustomersQuery.customers.numTotal}
									</Badge>
								</ReactCSSTransitionGroup>
							</If>
						</h2>

						<Choose>
							<When condition={myCustomers.length}>
								<div className="customer-list">
									{myCustomers.map(customer =>
										this.createCustomerComponent(customer, 'myCustomers'),
									)}
								</div>

								<If condition={!myCustomersQuery.loading}>
									<Pagination
										key="pagination"
										totalPages={Math.ceil(
											myCustomersQuery.customers.numTotal / DEFAULT_LIMIT,
										)}
										currentPage={parseInt(query.myCustomersPage, 10) || 1}
										onPageChange={page =>
											this.handleChangePage('myCustomers', page)
										}
									/>
								</If>
							</When>
							<Otherwise>No customers found.</Otherwise>
						</Choose>
					</div>
					<div className="customer-lists-middle">
						<If condition={this.props.user.can('edit-customer-list')}>
							<Icon type="arrow-right" onClick={() => this.handleClickUnlinkBtn()} />
							<Icon type="arrow-left" onClick={() => this.handleClickLinkBtn()} />
						</If>
					</div>
					<div className="customer-lists-right">
						<h2>
							All Customers
							<If condition={notMyCustomers.length}>
								<ReactCSSTransitionGroup
									transitionName="changed"
									transitionEnterTimeout={2000}
									transitionLeaveTimeout={100}>
									<Badge
										key={'badge-' + this.state.lastBulkEditTimestamp}
										shape="square"
										style="gray">
										{notMyCustomersQuery.customers.numTotal}
									</Badge>
								</ReactCSSTransitionGroup>
							</If>
						</h2>

						<Choose>
							<When condition={notMyCustomers.length}>
								<div className="customer-list">
									{notMyCustomers.map(customer =>
										this.createCustomerComponent(customer, 'notMyCustomers'),
									)}
								</div>

								<If
									condition={
										!notMyCustomersQuery.loading &&
										notMyCustomersQuery.customers.numTotal
									}>
									<Pagination
										key="pagination"
										totalPages={Math.ceil(
											notMyCustomersQuery.customers.numTotal / DEFAULT_LIMIT,
										)}
										currentPage={parseInt(query.notMyCustomersPage, 10) || 1}
										onPageChange={page =>
											this.handleChangePage('notMyCustomers', page)
										}
									/>
								</If>
							</When>
							<Otherwise>No customers found.</Otherwise>
						</Choose>
					</div>
				</div>

				<AddEditCustomerPane
					title={
						this.state.customer ? `Edit ${this.state.customer.name}` : 'Add a Customer'
					}
					visible={this.state.showAddEditPane}
					customer={this.state.customer}
					onClose={() => this.handleClosePane()}
					onSave={() => this.handleClosePane(true)}
				/>
				<LinkCustomerPane
					title={`Edit ${_.get(this.state.customer, 'name')} Data Elements`}
					visible={this.state.showLinkPane}
					customer={this.state.customer}
					onClose={() => this.handleClosePane()}
				/>
			</div>
		);
	}
}

export const myCustomersQuery = gql`
	query MyCustomersQuery($filter: CustomerFilters!, $limit: Int!, $skip: Int!) {
		customers(filter: $filter, limit: $limit, skip: $skip) {
			...CustomerResults
		}
	}
	${Customer.fragments.customer}
`;

export const notMyCustomersQuery = gql`
	query NotMyCustomersQuery($filter: CustomerFilters!, $limit: Int!, $skip: Int!) {
		customers(filter: $filter, limit: $limit, skip: $skip) {
			...CustomerResults
		}
	}
	${Customer.fragments.customer}
`;

export const deleteCustomerMutation = gql`
	mutation DeleteCustomer($id: EncodedID!) {
		deleteCustomer(id: $id) {
			id
		}
	}
`;

export const updateCustomersMutation = gql`
	mutation UpdateCustomers($customerIds: [EncodedID!]!, $userId: EncodedID) {
		updateCustomers(conditions: { id: { in: $customerIds } }, input: { managerUserId: $userId })
	}
`;

export default compose(
	connect(state => ({
		user: state.auth.user,
	})),
	graphql(myCustomersQuery, {
		name: 'myCustomersQuery',
		options: ({ user, location: { query } }) => ({
			fetchPolicy: 'network-only',
			variables: {
				filter: {
					deletedAt: { null: true },
					query: query.filter ? `%${query.filter}%` : undefined,
					managerUserId: {
						eq: user.id,
					},
				},
				limit: DEFAULT_LIMIT,
				skip: (query.myCustomersPage || 1) * DEFAULT_LIMIT - DEFAULT_LIMIT,
			},
		}),
	}),
	graphql(notMyCustomersQuery, {
		name: 'notMyCustomersQuery',
		options: ({ user, location: { query } }) => ({
			fetchPolicy: 'network-only',
			variables: {
				filter: {
					deletedAt: { null: true },
					query: query.filter ? `%${query.filter}%` : undefined,
					managerUserId: {
						ne: user.id,
					},
				},
				limit: DEFAULT_LIMIT,
				skip: (query.notMyCustomersPage || 1) * DEFAULT_LIMIT - DEFAULT_LIMIT,
			},
		}),
	}),
	graphql(deleteCustomerMutation, {
		props: ({ mutate }) => ({
			deleteCustomer: id =>
				mutate({
					variables: { id },
				}),
		}),
	}),
	graphql(updateCustomersMutation, {
		props: ({ mutate }) => ({
			updateCustomers: (customerIds, userId) =>
				mutate({
					variables: { customerIds, userId },
				}),
		}),
	}),
)(CustomerList);



// WEBPACK FOOTER //
// ./src/assets/js/containers/customers/List.js